import moment, { Moment } from 'moment';
import {
  Allocation,
  AllocationForTeam,
  AllocationForUser,
} from 'api/resource-management/types';
import { AllocationEventModel } from 'components/organisms/resource-management/scheduler-config/models';
import {
  maximumDate,
  minimumDate,
} from 'components/organisms/resource-management/utils/common';
import { AllocationEvent } from '../../event-types';
import { Range } from '../../types';

export const getMinimumStartDate = <
  T extends
    | AllocationForUser
    | AllocationForTeam
    | AllocationEventModel
    | Allocation
    | AllocationEvent,
>(
  allocations: T[],
): Moment =>
  allocations.length > 0
    ? allocations.reduce(
        (minDate: Moment, { startDate }) =>
          moment(startDate).isBefore(minDate, 'day')
            ? moment(startDate).startOf('day')
            : minDate,
        maximumDate,
      )
    : minimumDate;
export const getMaximumEndDate = <
  T extends
    | AllocationForUser
    | AllocationForTeam
    | AllocationEventModel
    | Allocation
    | AllocationEvent,
>(
  allocations: T[],
): Moment =>
  allocations.length > 0
    ? allocations.reduce(
        (maxDate: Moment, { endDate }) =>
          moment(endDate).isAfter(maxDate, 'day')
            ? moment(endDate).endOf('day')
            : maxDate,
        minimumDate,
      )
    : maximumDate;

// TODO: May have to place this logic in appropriate folder
export const getMinimumStartDateFromListOfRanges = (ranges: Range[]): Moment =>
  ranges.reduce(
    (minDate: Moment, { fromDate }) =>
      moment(fromDate).isBefore(minDate, 'day')
        ? moment(fromDate).startOf('day')
        : minDate,
    maximumDate,
  );

export const getMaximumEndDateFromListOfRanges = (ranges: Range[]): Moment =>
  ranges.reduce(
    (maxDate: Moment, { toDate }) =>
      moment(toDate).isAfter(maxDate, 'day')
        ? moment(toDate).endOf('day')
        : maxDate,
    minimumDate,
  );

export const getUserProjectLookupKey = (
  projectId: number,
  userId: number,
  referenceTeamId?: number,
) =>
  `USER:${userId}${
    referenceTeamId ? `-TEAM:${referenceTeamId}` : ''
  }-PROJECT:${projectId}`;

export const getBudgetLookupKey = (budgetId: number) => `BUDGET:${budgetId}`;

/**
 * Parses a resourceIdentifier string to extract userId and referenceTeamId.
 * Supports formats:
 * - USER:userId
 * - TEAM:teamId
 * - TEAM:teamId::User:userId
 */
export const parseResourceIdentifier = (
  resourceIdentifier: string,
): {
  userId?: number;
  referenceTeamId?: number;
  roleId?: number;
  teamId?: number;
} => {
  // Format: TEAM:teamId::User:userId
  const resolvedUserMatch = resourceIdentifier.match(
    /^TEAM:(\d+)::USER:(\d+)::ROLE:(\d+)$/,
  );

  if (resolvedUserMatch) {
    return {
      referenceTeamId: parseInt(resolvedUserMatch[1], 10),
      userId: parseInt(resolvedUserMatch[2], 10),
      roleId: parseInt(resolvedUserMatch[3], 10),
    };
  }

  // Format: USER:userId
  const userMatch = resourceIdentifier.match(/^USER:(\d+)$/);

  if (userMatch) {
    return {
      userId: parseInt(userMatch[1], 10),
    };
  }

  // Format: TEAM:teamId
  const teamMatch = resourceIdentifier.match(/^TEAM:(\d+)$/);

  if (teamMatch) {
    return {
      teamId: parseInt(teamMatch[1], 10),
    };
  }

  return {};
};
